"use client"

import { useState } from "react"

import Image, { type ImageProps } from "next/image"
import { twMerge } from "tailwind-merge"

import { focalPoint } from "@/platform/images/focal-points"

const TILE_BLUR_DATA_URL =
  "data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPSc0MCcgaGVpZ2h0PSc0MCc+PHJlY3Qgd2lkdGg9JzQwJyBoZWlnaHQ9JzQwJyBmaWxsPScjZWNlZWYwJy8+PC9zdmc+"

export const PRODUCT_PHOTO_QUALITY = 90

export interface ImageCrop {
  mobileUrl?: string | null
  focalX?: number | null
  focalY?: number | null
}

type TileImageProps = ImageProps & { crop?: ImageCrop }

const Placeholder = ({ alt, fill, className }: Pick<ImageProps, "alt" | "fill" | "className">) => (
  <div role="img" aria-label={alt} className={twMerge("bg-surface-newgray", fill ? "absolute inset-0" : "aspect-square w-full", className)} />
)

// Products without a thumbnail arrive as src="" — the browser treats that as a
// broken image, so hold the placeholder instead. A live src can also 404 (dead
// seller CDN entries are common), and an <img> that fails paints the browser's
// broken glyph plus the alt text over the card, so failures fall back too.
export const TileImage = ({ crop, ...props }: TileImageProps) => {
  const style = crop ? { ...props.style, objectPosition: `${crop.focalX ?? 50}% ${crop.focalY ?? 50}%` } : props.style
  if (crop?.mobileUrl && crop.mobileUrl !== props.src)
    return (
      <>
        <TileImageBase {...props} style={style} className={`${props.className ?? ""} hidden @3xl:block`} />
        <TileImageBase {...props} src={crop.mobileUrl} style={style} className={`${props.className ?? ""} @3xl:hidden`} />
      </>
    )
  return <TileImageBase {...props} style={style} />
}

const TileImageBase = ({ alt, src, style, fetchPriority, priority, preload, quality = PRODUCT_PHOTO_QUALITY, ...props }: ImageProps) => {
  const [failedSrc, setFailedSrc] = useState<ImageProps["src"]>()
  if (!src || failedSrc === src) return <Placeholder alt={alt} fill={props.fill} className={props.className} />
  // A crop is authored when the picture carries a registered focal point or the
  // caller states one. `50% 50%` written inline is the default, not a decision.
  const objectPosition = style?.objectPosition ?? focalPoint(src)
  const authored = objectPosition !== undefined && objectPosition !== "50% 50%" ? true : focalPoint(src) !== undefined
  // Next 16 split the old priority contract into `preload` plus an explicit
  // fetchpriority hint; without the hint the LCP image goes out at network
  // priority Low. Callers keep the familiar `priority`, translated here.
  const eager = preload || priority
  return (
    <Image
      alt={alt}
      src={src}
      placeholder="blur"
      blurDataURL={TILE_BLUR_DATA_URL}
      quality={quality}
      preload={eager}
      fetchPriority={fetchPriority ?? (eager ? "high" : undefined)}
      onError={() => setFailedSrc(src)}
      data-focal={authored ? "authored" : undefined}
      style={objectPosition ? { ...style, objectPosition } : style}
      {...props}
    />
  )
}
